// Copyright ©️ 2026 eVolve MEP, LLC

import type { BendTypeName } from '@evolvemep/api-ts';
import { Box, Text, UnstyledButton } from '@mantine/core';

import { isNil, isNotNil } from 'helpers/isNotNil';
import { useBendCart } from 'modules/Shop/EvolveFabrication/BendCreator/Cart/useBendCart';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import { bendTypeGlyphs, isOfferedBendType } from 'modules/Shop/EvolveFabrication/BendCreator/Nav/bendTypeGlyphs';
import classes from 'modules/Shop/EvolveFabrication/BendCreator/Nav/steps/steps.module.css';
import { getFreeName } from 'modules/Shop/EvolveFabrication/BendCreator/State/bendNaming';
import { canCenterBend } from 'modules/Shop/EvolveFabrication/BendCreator/State/centerBend';
import { useBendBuilder } from 'modules/Shop/EvolveFabrication/BendCreator/State/useBendBuilder';
import { useBendTypes } from 'modules/Shop/EvolveFabrication/BendCreator/useBendTypes';

export const BendTypeStep = () => {
  const { bendTypes } = useBendTypes();
  const form = useBendCreatorFormContext();
  const { scheduleValidation, resetLastValidation } = useBendCreatorValidationContext();
  const { isAutoNamed, setIsAutoNamed, editSession } = useBendBuilder();
  const { bends } = useBendCart();
  // A listed bend being edited doesn't take its own name, so renaming it can land on the same letter.
  const takenNames = bends
    .filter(({ cartBendId }) => cartBendId !== editSession?.bendSnapshot.cartBendId)
    .map(({ values }) => values.partName);

  const pickBendType = (bendTypeName: BendTypeName) => {
    if (form.values.bendType === bendTypeName) return;
    const newInputKeys = new Set(bendTypes?.find(({ name }) => name === bendTypeName)?.inputs.map((i) => i.keyName));
    form.setFieldValue('bendType', bendTypeName);
    form.clearFieldError('bendType');
    // Keep the dimensions the new bend type shares with the old one; clear the rest.
    bendTypes
      ?.find(({ name }) => name === form.values.bendType)
      ?.inputs.forEach(({ keyName }) => {
        if (newInputKeys.has(keyName)) return;
        form.setFieldValue(keyName, null);
        form.clearFieldError(keyName);
      });
    if (!canCenterBend(bendTypeName)) form.setFieldValue('centerBend', false);
    if (isAutoNamed || form.values.partName.trim() === '') {
      form.setFieldValue('partName', getFreeName(bendTypeName, takenNames));
      setIsAutoNamed(true);
    }
    resetLastValidation();
    scheduleValidation('bendType');
  };

  return (
    <Box>
      <Text className={classes.sectionLabel} mb={8}>
        Shape
      </Text>
      <Box className={classes.tiles} role="radiogroup" aria-label="Bend Type">
        {bendTypes?.map(({ name }) => {
          if (!isOfferedBendType(name)) return null;
          const { path, mirrored } = bendTypeGlyphs[name];
          const active = form.values.bendType === name;
          return (
            <UnstyledButton
              key={name}
              className={classes.tile}
              role="radio"
              aria-checked={active}
              data-active={active || undefined}
              onClick={() => pickBendType(name)}
            >
              <svg className={classes.glyph} viewBox="0 0 64 32" aria-hidden>
                <path d={path} transform={mirrored ? 'translate(64 0) scale(-1 1)' : undefined} />
              </svg>
              {name}
            </UnstyledButton>
          );
        })}
      </Box>
      {/* Once a bend type is picked, errors on it are about the geometry and show in the Dimensions step. */}
      {isNotNil(form.errors.bendType) && isNil(form.values.bendType) && (
        <Text c="red" fz="sm" mt="xs">
          {form.errors.bendType}
        </Text>
      )}
    </Box>
  );
};
